import React, {useMemo} from 'react';
import type {components} from '../../shared/api/v1/openapi.types';
import {
  CONNECTION_REASON_OPTIONS,
  CONNECTION_RELIABILITY_OPTIONS,
  getOptionLabel,
  isConnectionDraft,
} from '../../constants/connection';
import {useAuth} from '../../providers/auth/auth-provider';
import {Button} from 'primereact/button';

type ConnectionEntity = components['schemas']['Connection'];
type ConnectionPointPowerDto = components['schemas']['ConnectionPointPowerDto'];

type ConnectionPreviewProps = {
  connection: ConnectionEntity;
};

const guaranteeSupplier = 'ПАО "Красноярскэнергосбыт"';

const PointsList = ({points}: { points?: ConnectionPointPowerDto[] | null }) => {
  if (!points || points.length === 0) {
    return null;
  }
  return (
    <div>
      {points.map((point, index) => (
        <p key={`${point?.name ?? 'point'}-${index}`}>
          точка присоединения <b>{point?.name ?? '—'}</b> - <b>{point?.value ?? '—'}</b> кВт;
        </p>
      ))}
    </div>
  );
};

const Person15 = ({doc}: { doc: ConnectionEntity }) => {
  const partner = doc.partner;
  return (
    <div>
      <div className="connection-form-header">
        <div style={{ textTransform: 'uppercase' }}>Заявка</div>
        физического лица на присоединение по одному источнику электроснабжения
        энергопринимающих устройств с максимальной мощностью до 15 кВт
        включительно (используемых для бытовых и иных нужд, не связанных с
        осуществлением предпринимательской деятельности)
      </div>
      <div className="connection-form-body">
        <p>
          1. Наименование заявителя: <b>{partner?.name ?? '—'}</b>
        </p>
        {partner?.orgType === 0 ? (
          <p>
            2. Номер записи в Едином государственном реестре юридических лиц
            (номер записи в Едином государственном реестре индивидуальных
            предпринимателей) и дата ее внесения в реестр
            <b>{partner?.ogrn ?? '—'}</b> от <b>{partner?.creationDate ?? '—'}</b>
          </p>
        ) : partner?.orgType === 1 ? (
          <p>
            2. Паспортные данные серия, номер <b>{partner?.passportNum ?? '—'}</b> выдан (кем, когда)
            <b>{partner?.passportOrg ?? '—'}</b>
          </p>
        ) : null}
        <p>
          3,4. Место нахождения заявителя, в том числе фактический адрес
          <b>{partner?.address ?? '—'}</b>
        </p>
        <p>
          5. В связи с <b>{getOptionLabel(CONNECTION_REASON_OPTIONS, doc.reason)}</b> просит осуществить
          технологическое присоединение <b>{doc.devNames ?? '—'}</b> расположенных
          <b>{doc.address ?? '—'}</b>
        </p>
        <p>
          6. Максимальная мощность энергопринимающих устройств (присоединяемых и
          ранее присоединенных) <b>{doc.maxPower ?? '—'}</b> составляет кВт при
          напряжении <b>{doc.voltage1 ?? '—'}</b> кВ
        </p>
        <p>
          а) максимальная мощность присоединяемых энергопринимающих устройств
          составляет <b>{doc.powerOwened ?? '—'}</b> кВт при напряжении
          <b>{doc.voltage2 ?? '—'}</b> кВ
        </p>
        <p>
          б) максимальная мощность ранее присоединенных энергопринимающих
          устройств составляет <b>{doc.powerBefore ?? '—'}</b> кВт при напряжении
          <b>{doc.voltage3 ?? '—'}</b> кВ
        </p>
        <p>
          7. Заявляемая категория надежности энергопринимающих устройств –
          <b>{getOptionLabel(CONNECTION_RELIABILITY_OPTIONS, doc.reliability)}</b> (по одному источнику
          электроснабжения энергопринимающих устройств).
        </p>
        <p>
          8. Сроки проектирования и поэтапного введения в эксплуатацию объекта (в
          том числе по этапам и очередям), планируемое поэтапное распределение
          максимальной мощности: <b>{doc.dates ?? '—'}</b>
        </p>
        <p>
          9. Гарантирующий поставщик (энергосбытовая организация), с которым
          планируется заключение договора энергоснабжения (купли-продажи
          электрической энергии (мощности): <b>{guaranteeSupplier}</b>
        </p>
      </div>
    </div>
  );
};

const Simple = ({doc}: { doc: ConnectionEntity }) => {
  const partner = doc.partner;
  return (
    <div>
      <div className="connection-form-header">
        <div style={{ textTransform: 'uppercase' }}>Заявка</div>
        юридического лица (индивидуального предпринимателя), физического лица на
        присоединение энергопринимающих устройств
      </div>
      <div className="connection-form-body">
        <p>
          1. Наименование заявителя: <b>{partner?.name ?? '—'}</b>
        </p>
        {partner?.orgType === 0 ? (
          <p>
            2. Номер записи в Едином государственном реестре юридических лиц
            (номер записи в Едином государственном реестре индивидуальных
            предпринимателей) и дата ее внесения в реестр
            <b>{partner?.ogrn ?? '—'}</b> от <b>{partner?.creationDate ?? '—'}</b>
          </p>
        ) : partner?.orgType === 1 ? (
          <p>
            2. Паспортные данные серия, номер <b>{partner?.passportNum ?? '—'}</b> выдан (кем, когда)
            <b>{partner?.passportOrg ?? '—'}</b>
          </p>
        ) : null}
        <p>
          3. Место нахождения заявителя, в том числе фактический адрес
          <b>{partner?.address ?? '—'}</b>
        </p>
        <p>
          4. В связи с <b>{getOptionLabel(CONNECTION_REASON_OPTIONS, doc.reason)}</b> просит осуществить
          технологическое присоединение <b>{doc.devNames ?? '—'}</b> расположенных
          <b>{doc.address ?? '—'}</b>
        </p>
        <p>
          5. Количество точек присоединения с указанием технических параметров
          элементов энергопринимающих устройств <b>{doc.pointsNum ?? '—'}</b>
        </p>
        <p>
          6. Максимальная мощность энергопринимающих устройств (присоединяемых и
          ранее присоединенных) <b>{doc.maxPower ?? '—'}</b> составляет кВт при
          напряжении <b>{doc.voltage1 ?? '—'}</b> кВ
        </p>
        <p>
          а) максимальная мощность присоединяемых энергопринимающих устройств
          составляет <b>{doc.powerOwened ?? '—'}</b> кВт при напряжении
          <b>{doc.voltage2 ?? '—'}</b> кВ со следующим распределением по точкам
          присоединения:
        </p>
        <PointsList points={doc.pointsC as ConnectionPointPowerDto[]} />
        <p>
          б) максимальная мощность ранее присоединенных энергопринимающих
          устройств составляет <b>{doc.powerBefore ?? '—'}</b> кВт при напряжении
          <b>{doc.voltage3 ?? '—'}</b> кВ со следующим распределением по точкам
          присоединения:
        </p>
        <PointsList points={doc.pointsB as ConnectionPointPowerDto[]} />
        <p>
          7. Количество и мощность присоединяемых к сети трансформаторов
          <b>{doc.transNum ?? '—'}</b> кВА.
        </p>
        <p>
          8. Количество и мощность генераторов <b>{doc.genNum ?? '—'}</b>.
        </p>
        <p>9. Заявляемая категория надежности энергопринимающих устройств:</p>
        <p>
          I категория <b>{doc.level1 ?? '—'}</b> кВт;
        </p>
        <p>
          II категория <b>{doc.level2 ?? '—'}</b> кВт;
        </p>
        <p>
          III категория <b>{doc.level3 ?? '—'}</b> кВт;
        </p>
        <p>
          10. Заявляемый характер нагрузки (для генераторов – возможная скорость
          набора или снижения нагрузки) и наличие нагрузок, искажающих форму
          кривой электрического тока и вызывающих несимметрию напряжения в точках
          присоединения <b>{doc.loadNatureFull ?? '—'}</b>
        </p>
        <p>
          11. Величина и обоснование величины технологического минимума (для
          генераторов) <b>{doc.generator ?? '—'}</b>
        </p>
        <p>
          12. Необходимость наличия технологической и (или) аварийной брони
          <b>{doc.armor ?? '—'}</b>
        </p>
        <p>
          Величина и обоснование технологической и аварийной брони
          <b>{doc.armorValue ?? '—'}</b>
        </p>
        <p>
          13. Сроки проектирования и поэтапного введения в эксплуатацию объекта (в
          том числе по этапам и очередям), планируемое поэтапное распределение
          максимальной мощности: <b>{doc.dates ?? '—'}</b>
        </p>
        <p>
          14. Гарантирующий поставщик (энергосбытовая организация), с которым
          планируется заключение договора энергоснабжения (купли-продажи
          электрической энергии (мощности): <b>{guaranteeSupplier}</b>
        </p>
      </div>
    </div>
  );
};

const Simple150 = ({doc}: { doc: ConnectionEntity }) => {
  const partner = doc.partner;
  return (
    <div>
      <div className="connection-form-header">
        <div style={{ textTransform: 'uppercase' }}>Заявка</div>
        юридического лица (индивидуального предпринимателя), физического лица на
        присоединение по одному источнику электроснабжения энергопринимающих
        устройств с максимальной мощностью до 150 кВт включительно
      </div>
      <div className="connection-form-body">
        <p>
          1. Наименование заявителя: <b>{partner?.name ?? '—'}</b>
        </p>
        {partner?.orgType === 0 ? (
          <p>
            2. Номер записи в Едином государственном реестре юридических лиц
            (номер записи в Едином государственном реестре индивидуальных
            предпринимателей) и дата ее внесения в реестр
            <b>{partner?.ogrn ?? '—'}</b> от <b>{partner?.creationDate ?? '—'}</b>
          </p>
        ) : partner?.orgType === 1 ? (
          <p>
            2. Паспортные данные серия, номер <b>{partner?.passportNum ?? '—'}</b> выдан (кем, когда)
            <b>{partner?.passportOrg ?? '—'}</b>
          </p>
        ) : null}
        <p>
          3. Место нахождения заявителя, в том числе фактический адрес
          <b>{partner?.address ?? '—'}</b>
        </p>
        <p>
          4. В связи с <b>{getOptionLabel(CONNECTION_REASON_OPTIONS, doc.reason)}</b> просит осуществить
          технологическое присоединение <b>{doc.devNames ?? '—'}</b> расположенных
          <b>{doc.address ?? '—'}</b>
        </p>
        <p>
          5. Максимальная мощность энергопринимающих устройств (присоединяемых и
          ранее присоединенных) <b>{doc.maxPower ?? '—'}</b> составляет кВт при
          напряжении <b>{doc.voltage1 ?? '—'}</b> кВ
        </p>
        <p>
          а) максимальная мощность присоединяемых энергопринимающих устройств
          составляет <b>{doc.powerOwened ?? '—'}</b> кВт при напряжении
          <b>{doc.voltage2 ?? '—'}</b> кВ
        </p>
        <p>
          б) максимальная мощность ранее присоединенных энергопринимающих
          устройств составляет <b>{doc.powerBefore ?? '—'}</b> кВт при напряжении
          <b>{doc.voltage3 ?? '—'}</b> кВ
        </p>
        <p>
          6. Заявляемая категория надежности энергопринимающих устройств –
          <b>{getOptionLabel(CONNECTION_RELIABILITY_OPTIONS, doc.reliability)}</b> (по одному источнику
          электроснабжения энергопринимающих устройств).
        </p>
        <p>
          7. Характер нагрузки (вид экономической деятельности заявителя)
          <b>{doc.loadNatureFull ?? '—'}</b>
        </p>
        <p>
          8. Сроки проектирования и поэтапного введения в эксплуатацию объекта (в
          том числе по этапам и очередям), планируемое поэтапное распределение
          максимальной мощности: <b>{doc.dates ?? '—'}</b>
        </p>
        <p>
          9. Порядок расчета и условия рассрочки внесения платы за технологическое
          присоединение по договору осуществляются <b>{doc.offers ?? '—'}</b>
        </p>
        <p>
          10. Гарантирующий поставщик (энергосбытовая организация), с которым
          планируется заключение договора энергоснабжения (купли-продажи
          электрической энергии (мощности): <b>{guaranteeSupplier}</b>
        </p>
      </div>
    </div>
  );
};

const Temp = ({doc}: { doc: ConnectionEntity }) => {
  const partner = doc.partner;
  return (
    <div>
      <div className="connection-form-header">
        <div style={{ textTransform: 'uppercase' }}>Заявка</div>
        юридического лица (индивидуального предпринимателя), физического лица на
        временное присоединение энергопринимающих устройств
      </div>
      <div className="connection-form-body">
        <p>
          1. Наименование заявителя: <b>{partner?.name ?? '—'}</b>
        </p>
        {partner?.orgType === 0 ? (
          <p>
            2. Номер записи в Едином государственном реестре юридических лиц
            (номер записи в Едином государственном реестре индивидуальных
            предпринимателей) и дата ее внесения в реестр
            <b>{partner?.ogrn ?? '—'}</b> от <b>{partner?.creationDate ?? '—'}</b>
          </p>
        ) : partner?.orgType === 1 ? (
          <p>
            2. Паспортные данные серия, номер <b>{partner?.passportNum ?? '—'}</b> выдан (кем, когда)
            <b>{partner?.passportOrg ?? '—'}</b>
          </p>
        ) : null}
        <p>
          3. Место нахождения заявителя, в том числе фактический адрес
          <b>{partner?.address ?? '—'}</b>
        </p>
        <p>
          4. В связи с <b>{getOptionLabel(CONNECTION_REASON_OPTIONS, doc.reason)}</b> просит осуществить
          технологическое присоединение <b>{doc.devNames ?? '—'}</b> расположенных
          <b>{doc.address ?? '—'}</b>
        </p>
        <p>
          5. Максимальная мощность энергопринимающих устройств
          <b>{doc.maxPower ?? '—'}</b> составляет кВт при напряжении
          <b>{doc.voltage1 ?? '—'}</b> кВ
        </p>
        <p>
          6. Характер нагрузки (вид экономической деятельности заявителя)
          <b>{doc.loadNatureFull ?? '—'}</b>
        </p>
        <p>
          7. Срок электроснабжения по временной схеме <b>{doc.tempDates ?? '—'}</b>
        </p>
        <p>
          8. Реквизиты договора на технологическое присоединение:
          <b>{doc.contractInfo ?? '—'}</b>
        </p>
        <p>
          9. Гарантирующий поставщик (энергосбытовая организация), с которым
          планируется заключение договора энергоснабжения (купли-продажи
          электрической энергии (мощности): <b>{guaranteeSupplier}</b>
        </p>
      </div>
    </div>
  );
};

export const ConnectionPreview = ({connection}: ConnectionPreviewProps) => {
  const {user} = useAuth();
  const isEmployee = user?.type === 'employee';
  const content = useMemo(() => {
    switch (connection.type) {
      case 1:
        return <Temp doc={connection} />;
      case 2:
        return <Simple doc={connection} />;
      case 3:
        return <Simple150 doc={connection} />;
      case 4:
        return <Person15 doc={connection} />;
      default:
        return (
          <div>
            <div className="connection-form-header">Заявление на технологическое присоединение</div>
            <div className="connection-form-body">
              <p>Заявитель: <b>{connection.partner?.name || '—'}</b></p>
              <p>Энергопринимающие устройства: <b>{connection.devNames || '—'}</b></p>
              <p>Регион: <b>{connection.region || '—'}</b></p>
              <p>Адрес: <b>{connection.address || '—'}</b></p>
              <p>Максимальная мощность: <b>{connection.maxPower || '—'}</b> кВт</p>
            </div>
          </div>
        );
    }
  }, [connection]);

  return (
    <div>
      <style>{`
        .show-on-print {
          display: none;
        }
        @media print {
          .show-on-print {
            display: block;
          }
          .text-to-print {
            background-color: white;
            height: 100%;
            width: 100%;
            position: fixed;
            top: 0;
            left: 0;
            margin: 0;
            padding: 15px;
            font-size: 14px;
            line-height: 18px;
            z-index: 9999999;
          }
        }
        .connection-form-header {
          padding: 20px 0;
          text-align: center;
          font-family: 'Times New Roman', Times, serif;
          font-weight: bold;
          font-size: 18px;
          color: black;
        }
        .connection-form-body {
          text-align: left;
          font-family: 'Times New Roman', Times, serif;
          font-size: 16px;
          color: black;
        }
      `}</style>
      <div className="text-to-print">
        {content}
        {isEmployee ? (
          <div className="mt-3 p-3 border-round border-1 surface-border">
            <div className="text-xl font-semibold mb-2">Контактная информация</div>
            <div className="text-color-secondary mb-2">Контрагента:</div>
            <div>
              Телефон: {connection.partner?.telephone ?? '—'}
              <br />
              E-mail: {connection.partner?.email ?? '—'}
            </div>
          </div>
        ) : null}
        {isEmployee && !isConnectionDraft(connection.status) ? (
          <div className="mt-3 p-3 border-round border-1 surface-border show-on-print">
            <div className="text-xl font-semibold mb-2">Подпись</div>
            <div>Заявление подано электронно через портал sibseti.su</div>
          </div>
        ) : null}
      </div>
      {!isConnectionDraft(connection.status) ? <div className="mt-3">
        <Button
          outlined
          size="small"
          icon="pi pi-print"
          label="Распечатать заявку"
          onClick={() => window.print()}
        />
      </div> : null}
    </div>
  );
};
